import React from 'react';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';

interface Button {
  label: string;
  link?: string;
}

interface Project {
  title: string;
  category: string;
  image: string;
  link: string;
  buttons: Button[];
}

const projects: Project[] = [
  {
    title: 'Pitstop',
    category: 'Web Design',
    image: "/Images/whypitstop.png",
    link: "#",
    buttons: [
      { label: "User Experience", link: "#" },
      { label: "Design System" },
      { label: "Not Live" }
    ]
  },
  {
    title: 'Dialinger',
    category: 'App Design',
    image: "/Images/dialinger.png" ,
    link: "https://app.dialinger.com/",
    buttons: [
      { label: "User Experience", },
      { label: "Design System" },
      { label: "Live on Play Store/ App Store" }
    
    ]
  },
  {
    title: 'Himalayan Pathways',
    category: 'Web Design',
    image: "/Images/Project1.png",
    link: "https://himalayanpathways.co/",
    buttons: [
      { label: "Live Site", link: "https://himalayanpathways.co/" },
      { label: "Mobile Responsive" },
      { label: "Creative Design" },
      { label: "Tech Nirvana", link: "https://www.technirvana.com.np/" },
    ]
  },
  {
    title: 'EV Charging',
    category: 'Web Design',
    image: "/Images/EV1.png",
    link: "https://ev-pi.vercel.app/",
    buttons: [
      { label: "Live Site", link: "https://ev-pi.vercel.app/" },
      { label: "Product Design" },
      { label: "User Interface" }
    ]
  },
  {
    title: 'Hajir App',
    category: 'APP DESIGN',
    image: "/Images/hajirmobile.png",
    link: "https://hajirapp.com/download/",
    buttons: [
      { label: "Live on Play Store/ App Store", link: "https://hajirapp.com/download/" },
      { label: "Product Design" },
      { label: "User Interface" },
      { label: "UX Research" },
      { label: "Velox Labs", link: "https://velox.com.np/" },

    ]
  },

  {
    title: 'Hajir Web',
    category: 'Web Design',
    image: "/Images/hajirweb1.png" ,
    link: "https://hajirapp.com/",
    buttons: [
      { label: "Live", link: "https://hajirapp.com/" },
      { label: "User Experience", },
      { label: "Design System" },
      { label: "Velox Labs", link: "https://velox.com.np/" },
    
    ]
  }
];

const WorkSection: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Simplified animation variants for better performance
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.4,
        staggerChildren: 0.1 // Reduced stagger time
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 }, // Reduced movement
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 font-sans" data-bg="light">
      <div className="max-w-8xl mx-auto">
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }} // Reduced duration
          ref={ref}
        >
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[20px] text-white">Explore my recent projects</p>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {projects.map((project, index) => (
            <motion.div 
              key={index} 
              className="group relative overflow-hidden rounded-lg"
              variants={itemVariants}
              transition={{ duration: 0.4 }} // Reduced duration
            >
              <a href={project.link} target="_blank" rel="noopener noreferrer">
                <div className="relative overflow-hidden aspect-[4/3] rounded-lg">
                  <img 
                    src={project.image}
                    alt={project.title}
                    loading="lazy" // Add lazy loading
                    className="w-full h-full object-cover object-center transform group-hover:scale-[1.01] transition-transform duration-300 rounded-lg" // Reduced scale and duration
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg" /> {/* Reduced duration */}
                </div>
              </a>
              
              <div className="p-6 bg-black relative">
                <div className="text-sm uppercase tracking-wider mb-2 text-white">{project.category}</div>
                <h3 className="text-xl font-bold text-gray-400">{project.title}</h3>
                <div className="hidden sm:flex absolute bottom-4 right-4 gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200"> {/* Reduced duration */}
                  {project.buttons.map((btn, i) => (
                    btn.link ? (
                      <a
                        key={i}
                        href={btn.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 bg-white text-black rounded-full font-semibold shadow-lg hover:bg-gray-100 transition-colors duration-200 text-xs md:text-sm" // Reduced duration
                      >
                        {btn.label}
                      </a>
                    ) : (
                      <button
                        key={i}
                        className="px-3 py-1.5 bg-gray-200 text-black rounded-full font-semibold shadow-lg cursor-default text-xs md:text-sm"
                        type="button"
                      >
                        {btn.label}
                      </button>
                    )
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default WorkSection;